/**
 * LeadCard - Unified Lead/Contact/Company Display Card
 * 
 * Extracted from FeedHome.js for use across all views.
 * Adapts to people, company, job, or signal results.
 * 
 * SINGLE SOURCE OF TRUTH for lead card rendering.
 * Used by: FeedHome, FeedWorkspace, and any other feed view.
 */

import React, { useState } from 'react';
import {
  Briefcase,
  Crown,
  Building2,
  TrendingUp,
  MapPin,
  Mail,
  Phone,
  Linkedin,
  ExternalLink,
  Users,
  Target,
  DollarSign,
  Clock,
  Globe,
  Zap,
  GraduationCap,
  Check,
  X,
  Loader2,
  Send,
  MessageSquare,
  CircleDot,
  CheckCircle2,
  XCircle,
  Sparkles,
  Search,
  Flame,
} from 'lucide-react';
import api from '../../utils/axios';
import { useFieldVisibilityOptional } from '../../contexts/FieldVisibilityContext';
import { useQuickSearch } from '../../contexts/QuickSearchContext';
import { extractKeyElements, extractKeyElementsForSimilar, convertKeyElementsToFilters, getSimilarLookupExplanation } from '../../utils/extractKeyElements';
import { DemandZoneBadge } from './DemandZoneBadge';
import { HiringPredictionCard } from './HiringPredictionCard';
import { SimpleReach } from '../reach';

const REACH_ENABLED = true;

// =============================================================================
// CLICKABLE CONTEXT LINK - Triggers quick search popup
// Simple, clean hover effect - no distortion
// =============================================================================
interface ContextLinkProps {
  type: 'company' | 'person' | 'job' | 'news' | 'skill' | 'industry';
  data: Record<string, any>;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
}

function ContextLink({ type, data, children, className = '', onClick }: ContextLinkProps) {
  const { triggerQuickSearch } = useQuickSearch();
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClick) {
      onClick(e);
    }
    triggerQuickSearch(type, data, e);
  };
  
  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1 cursor-pointer hover:text-[var(--eq-purple-text)] hover:underline decoration-[var(--eq-purple)]/30 underline-offset-2 transition-colors ${className}`}
    >
      <span>{children}</span>
      <Search className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />
    </button>
  );
}

// =============================================================================
// DATE FORMATTING HELPER - Convert ISO dates to human-readable format
// =============================================================================
const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr; // Return as-is if invalid
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  } catch {
    return dateStr;
  }
};

const toText = (value: any, fallback = ''): string => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    return value.name || value.title || value.label || value.source || fallback;
  }
  return fallback;
};

// =============================================================================
// HIGHLIGHT HELPER - Highlight keywords in text with background color
// =============================================================================
interface HighlightedTextProps {
  text: string;
  keywords: string[];
  className?: string;
}

function HighlightedText({ text, keywords, className = '' }: HighlightedTextProps) {
  if (!text || !keywords || keywords.length === 0) {
    return <span className={className}>{text}</span>;
  }

  // Create regex pattern for all keywords (case insensitive)
  const pattern = keywords
    .filter(k => k && k.length > 0)
    .map(k => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // Escape regex special chars
    .join('|');
  
  if (!pattern) {
    return <span className={className}>{text}</span>;
  }

  const regex = new RegExp(`(${pattern})`, 'gi');
  const parts = text.split(regex);

  return (
    <span className={className}>
      {parts.map((part, i) => {
        const isMatch = keywords.some(k => k.toLowerCase() === part.toLowerCase());
        return isMatch ? (
          <mark key={i} className="bg-amber-200 text-amber-900 px-0.5 rounded-sm font-semibold">
            {part}
          </mark>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </span>
  );
}

const parseRawData = (value: any): Record<string, any> | null => {
  if (!value) return null;
  if (typeof value === 'object') return value as Record<string, any>;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
};

// =============================================================================
// FAILED LOGO CACHE - Prevents re-requesting known failed logos
// Module-level cache shared across all LeadCard instances
// =============================================================================
const failedLogoCache = new Set<string>();

// =============================================================================
// COMPANY LOGO WITH FALLBACK - Reusable component for real company logos
// =============================================================================
function CompanyLogoWithFallback({ 
  logoUrl, 
  companyName, 
  size = 'md' 
}: { 
  logoUrl: string; 
  companyName: string; 
  size?: 'sm' | 'md' | 'lg';
}) {
  // Check cache first - if URL already failed, skip the request
  const alreadyFailed = failedLogoCache.has(logoUrl);
  const [failed, setFailed] = useState(alreadyFailed);
  
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-14 h-14',
  };
  
  const handleLogoError = () => {
    // Add to cache so other components don't try this URL
    failedLogoCache.add(logoUrl);
    setFailed(true);
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-lg overflow-hidden bg-white flex-shrink-0 border border-slate-200 shadow-sm flex items-center justify-center`}>
      {!failed ? (
        <img 
          src={logoUrl}
          alt={companyName}
          className="w-full h-full object-contain p-1"
          onError={handleLogoError}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center text-white text-sm font-bold">
          {companyName.charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// LEAD QUALIFICATION STATUSES - Like / Skip
// =============================================================================
export const LEAD_STATUSES = [
  { id: 'inbox', label: 'New', color: '#5a20ff', icon: CircleDot },
  { id: 'liked', label: 'Liked', color: '#10b981', icon: CheckCircle2 },
  { id: 'archived', label: 'Archived', color: '#6b7280', icon: XCircle },
];

export type LeadStatus = typeof LEAD_STATUSES[number];

// =============================================================================
// TYPES
// =============================================================================
export interface LeadCardProps {
  lead: any;
  isSelected?: boolean;
  onSelect?: () => void;
  status?: string;
  onStatusChange?: (status: string) => void;
  customFields?: any[];
  isBulkMode?: boolean;
  isBulkSelected?: boolean;
  onBulkToggle?: () => void;
  searchMode?: 'people' | 'company' | 'jobs' | 'demand';
  onFindContacts?: (company: string, contacts: any[]) => void;
  onFindDecisionMaker?: (company: string, dm: any) => void;
  onRefineSearch?: (context: string, data: any) => void;
  onFindSimilar?: (filters: any[], keyElements: any) => void; // Find Similar callback
  enabledFields?: Record<string, boolean>;
  onLike?: (leadId: string, comment: string) => void;
  likeCount?: number;
  isLiked?: boolean;
  // Tinder-style swipe actions
  onSwipeLike?: (leadId: string) => void;
  onSwipeDislike?: (leadId: string) => void;
  showSwipeActions?: boolean;
  // Auto-advance to next card after action
  onNextLead?: () => void;
  // Guest mode - blur contact details and show sign-up prompt
  isGuest?: boolean;
  onGuestClick?: () => void;
  // Index for blurring (only first card visible for guests)
  cardIndex?: number;
  // Keyword match data from useKeywordScoring
  keywordMatches?: {
    jobTitle: string[];
    companyName: string[];
    companyDescription: string[];
    primaryIndustry: string[];
  } | null;
  hasKeywordMatch?: boolean;
}

// =============================================================================
// LEAD CARD COMPONENT
// =============================================================================
export function LeadCard({ 
  lead, 
  isSelected = false, 
  onSelect, 
  status = 'inbox', 
  onStatusChange, 
  customFields = [], 
  isBulkMode = false, 
  isBulkSelected = false, 
  onBulkToggle,
  searchMode = 'people',
  onFindContacts,
  onFindDecisionMaker,
  onRefineSearch,
  onFindSimilar,
  enabledFields = {},
  onLike,
  likeCount = 0,
  isLiked = false,
  onNextLead,
  isGuest = false,
  onGuestClick,
  cardIndex = 0,
  keywordMatches = null,
  hasKeywordMatch = false,
}: LeadCardProps) {
  // Guest mode: blur cards after the first one, gate all interactions
  const shouldBlur = isGuest && cardIndex > 0;
  const shouldGateClick = isGuest;
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [foundContacts, setFoundContacts] = useState<any[]>([]);
  const [showLikeModal, setShowLikeModal] = useState(false);
  const [likeComment, setLikeComment] = useState('');
  const [showContacts, setShowContacts] = useState(false);
  
  const [showReachModal, setShowReachModal] = useState(false);
  const [revealedEmail, setRevealedEmail] = useState('');

  // Tinder-style animation states
  const [animationState, setAnimationState] = useState<'idle' | 'liking' | 'archiving'>('idle');
  const [isExiting, setIsExiting] = useState(false);
  
  // Get field visibility from context (optional - will work without provider)
  const fieldVisibility = useFieldVisibilityOptional();
  
  // Helper to check if a field should be shown
  // Uses context if available, falls back to enabledFields prop, then defaults to true
  const isFieldVisible = (fieldId: string, category: 'people' | 'company' | 'jobs' | 'agent'): boolean => {
    // First check the prop (allows direct override)
    if (enabledFields && fieldId in enabledFields) {
      return enabledFields[fieldId];
    }
    // Then check context
    if (fieldVisibility) {
      return fieldVisibility.isFieldEnabled(fieldId, category);
    }
    // Default to true if no configuration
    return true;
  };

  // Extract lead data from various possible structures
  const rawData = parseRawData(lead.raw_data || lead.rawData);
  const data =
    lead.data ||
    lead.lead_data ||
    (rawData ? { ...rawData, ...lead } : lead);
  
  // Detect result type - SIGNAL type for demand search includes searchMode check
  // Company mode shows people results with company-prominent layout (NOT old company entity cards)
  const leadType = lead.type || lead.lead_type;
  const isSignalResult = leadType === 'signal' || leadType === 'signals' || searchMode === 'demand';
  const isCompanyModeResult = searchMode === 'company' && !isSignalResult && leadType !== 'company';
  const isCompanyResult = !isSignalResult && !isCompanyModeResult && (leadType === 'company' || !!data.companySize || !!data.revenue);
  const isJobResult = !isSignalResult && (leadType === 'job' || leadType === 'jobs' || searchMode === 'jobs' || !!data.postedDate);
  const isNewsResult = leadType === 'news' || !!data.publishedDate || !!data.articleUrl;
  
  const firstName = data.firstName || data.first_name || lead.first_name || '';
  const lastName = data.lastName || data.last_name || lead.last_name || '';
  const fullName = data.name || data.fullName || `${firstName} ${lastName}`.trim() || '';
  const jobTitle = data.jobTitle || data.title || data.job_title || lead.job_title || '';
  
  // Extract the ACTUAL COMPANY mentioned in the article (NOT the news source)
  // ZIA: Real data over fake - "Fierce Biotech" is the news source, not the company
  const extractCompanyFromSignal = (): string => {
    // Direct company fields first (most reliable)
    if (data.companyName) return data.companyName;
    if (data.company_name) return data.company_name;
    if (lead.company_name) return lead.company_name;
    if (data.organization) return data.organization;
    
    // Try to extract the company DOING the action from title/snippet
    const title = data.title || data.signalTitle || data.headline || '';
    const snippet = data.snippet || data.signalSnippet || data.body || '';
    
    // News source keywords to EXCLUDE (these are publishers, not companies)
    const newsSourceKeywords = ['biotech', 'pharma times', 'fierce', 'news', 'today', 'daily', 'times', 'journal', 'post', 'wire', 'reuters', 'bloomberg', 'firstword', 'pbs', 'bbc', 'cnn', 'nbc', 'abc', 'fox', 'yahoo', 'google', 'msn'];
    
    if (title) {
      // Pattern: "CompanyName raises/announces/launches/gets..."
      const actionMatch = title.match(/^([A-Z][a-zA-Z0-9\s&'.-]+?)(?:\s+(?:raises|announces|launches|secures|receives|expands|acquires|partners|hires|closes|opens|reports|gets|wins|completes|signs|files|submits|approved|FDA))/i);
      if (actionMatch && actionMatch[1].length < 50 && actionMatch[1].length > 2) {
        const extracted = actionMatch[1].trim();
        // Verify it's not a news source
        if (!newsSourceKeywords.some(k => extracted.toLowerCase().includes(k))) {
          return extracted;
        }
      }
      
      // Pattern: "FDA approves Company's drug..."
      const fdaMatch = title.match(/FDA\s+(?:approves?|clears?|grants?)\s+([A-Z][a-zA-Z0-9\s&'.-]+?)(?:'s|'|\s+drug|\s+device|\s+therapy|\s+treatment)/i);
      if (fdaMatch && fdaMatch[1].length < 50) {
        return fdaMatch[1].trim();
      }
    }
    
    // Try snippet for company mentions
    if (snippet) {
      // Pattern: "Company, a biotech/pharma company..."
      const descMatch = snippet.match(/([A-Z][a-zA-Z0-9\s&'.-]+?),?\s+(?:a\s+)?(?:biotech|biopharmaceutical|pharmaceutical|medical device|healthcare|therapeutics)/i);
      if (descMatch && descMatch[1].length < 50 && descMatch[1].length > 2) {
        const extracted = descMatch[1].trim();
        if (!newsSourceKeywords.some(k => extracted.toLowerCase().includes(k))) {
          return extracted;
        }
      }
    }
    
    // DON'T fall back to news source - return empty so UI handles it
    return '';
  };
  
  // Extract company name - check nested company object too (ZoomInfo stores it there)
  const extractedCompanyName = (() => {
    if (data.companyName) return data.companyName;
    if (data.company_name) return data.company_name;
    if (lead.company_name) return lead.company_name;
    // Check nested company object (ZoomInfo format)
    if (typeof data.company === 'object' && data.company?.name) return data.company.name;
    if (typeof data.company === 'string') return data.company;
    return '';
  })();
  const companyName = isSignalResult ? extractCompanyFromSignal() : extractedCompanyName;
  const email = data.email || lead.email || lead.lead_email || '';
  const phone = data.phone || lead.phone || '';
  // Location: city + state, or standalone location/country so cards show something when available
  const location = (() => {
    if (data.city && data.state) return `${data.city}, ${data.state}`;
    if (data.city) return data.state ? `${data.city}, ${data.state}` : data.city;
    if (data.state) return data.state;
    if (data.location && typeof data.location === 'string') return data.location;
    if (data.country) return data.country;
    if (data.metroRegion) return data.metroRegion;
    return '';
  })();
  // Safely extract industry - handle case where it might be an object
  const rawIndustry = data.industry || lead.industry || '';
  const industry = typeof rawIndustry === 'object' ? (rawIndustry as any)?.name || '' : rawIndustry;
  
  // Company-specific fields - check both flat and nested company object
  const companyObj = data.company || {};
  const website = data.website || data.companyWebsite || companyObj.website || data.domain || '';
  const employeeCount = data.employeeCount || data.employees || data.companySize ||
    companyObj.employeeCount || companyObj.size || data.companyEmployeeCount || '';
  const revenue = data.revenue || data.companyRevenue || companyObj.revenue || '';
  const companyIndustry = data.companyIndustry || data.industry || companyObj.industry || companyObj.primaryIndustry || data.companyPrimaryIndustry || '';
  const companyCity = data.companyCity || companyObj.address?.city || '';
  const companyState = data.companyState || companyObj.address?.state || '';
  
  // Job-specific fields  
  const salary = data.salary || data.salaryRange || '';
  const postedDate = data.postedDate || data.posted_at || '';
  const sourceUrl = data.sourceUrl || data.job_link || data.link || '';
  const remoteType = data.remoteType || '';
  
  // Signal-specific fields (from SERP demand search)
  const signalType = toText(data.signalType || data.type);
  // Get the best available snippet/summary - try multiple SERP fields
  const signalSnippet = toText(
    data.signalSnippet ||
      data.snippet ||
      data.body ||
      data.content ||
      data.highlight ||
      data.description ||
      data.summary
  );
  // Get the headline - this is the most important for news/funding
  const signalHeadline = toText(data.headline || data.title || data.name, fullName || '');
  // Funding amount if available
  const fundingAmount = data.amount || data.fundingAmount || data.raised || '';
  const signalDate = toText(
    data.signalDate || data.date || data.publishedDate || data.published_at
  );
  // Ensure signalSource is a string (data.source might be an object with various structures)
  const signalSource = (() => {
    if (typeof data.signalSource === 'string') return data.signalSource;
    if (data.signalSource && typeof data.signalSource === 'object') {
      return data.signalSource.name || data.signalSource.title || data.signalSource.source || '';
    }
    if (typeof data.source === 'string') return data.source;
    if (typeof data.publisher === 'string') return data.publisher;
    if (data.source && typeof data.source === 'object') {
      return data.source.name || data.source.title || data.source.source || '';
    }
    return '';
  })();
  const signalSourceUrl = typeof data.sourceUrl === 'string' ? data.sourceUrl 
    : typeof data.link === 'string' ? data.link 
    : typeof data.url === 'string' ? data.url 
    : data.source?.link || '';
  const signalSourceLogo = typeof data.sourceLogo === 'string' ? data.sourceLogo 
    : data.source?.thumbnail || '';
  const demandScore = data.demandScore || 0;
  const demandZone = data.demandZone || '';
  
  // Hiring prediction from ripple-logic (for staffing agencies)
  const hiringPrediction = data.hiringPrediction || null;
  const expectedHires = data.expectedHires || hiringPrediction?.expectedHires || null;
  const outreachTiming = data.outreachTiming || hiringPrediction?.outreachTiming || null;
  const signalModeId = data.signalModeId || '';
  
  // Check if signal is "New" (within last 24 hours)
  const isNewSignal = React.useMemo(() => {
    if (!signalDate) return false;
    try {
      const articleDate = new Date(signalDate);
      const now = new Date();
      const hoursDiff = (now.getTime() - articleDate.getTime()) / (1000 * 60 * 60);
      return hoursDiff <= 24;
    } catch {
      return false;
    }
  }, [signalDate]);

  
  // Profile picture and company logo
  const rawProfilePicture = data.profilePicture || data.profilePictureUrl || data.picture ||
    data.imageUrl || data.personImageUrl || data.photo || data.avatar || null;
  const profilePicture = rawProfilePicture || null;
  
  // Extract domain for logo API fallback
  const extractDomain = (url: string): string => {
    if (!url) return '';
    try {
      // Handle URLs with or without protocol
      const urlWithProtocol = url.startsWith('http') ? url : `https://${url}`;
      const parsed = new URL(urlWithProtocol);
      return parsed.hostname.replace('www.', '');
    } catch {
      // If URL parsing fails, try to extract domain directly
      return url.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
    }
  };
  
  // Generate company name-based domain guess - ONLY for well-known companies
  // Skip guessing for local/small businesses to avoid 404 spam
  const guessCompanyDomain = (name: string): string => {
    if (!name) return '';
    
    // Common company name to domain mappings - only well-known companies
    const domainMappings: Record<string, string> = {
      'wells fargo': 'wellsfargo.com',
      'kaiser permanente': 'kaiserpermanente.org',
      'td synnex': 'tdsynnex.com',
      'synnex': 'synnex.com',
      'university of california': 'universityofcalifornia.edu',
      'us airways': 'usairways.com',
      'uber': 'uber.com',
      'visa': 'visa.com',
      'google': 'google.com',
      'microsoft': 'microsoft.com',
      'apple': 'apple.com',
      'amazon': 'amazon.com',
      'meta': 'meta.com',
      'facebook': 'facebook.com',
      'netflix': 'netflix.com',
      'salesforce': 'salesforce.com',
      'oracle': 'oracle.com',
      'ibm': 'ibm.com',
      'intel': 'intel.com',
      'cisco': 'cisco.com',
      'adobe': 'adobe.com',
      'nvidia': 'nvidia.com',
      'paypal': 'paypal.com',
      'stripe': 'stripe.com',
      'slack': 'slack.com',
      'zoom': 'zoom.us',
      'shopify': 'shopify.com',
      'twitter': 'twitter.com',
      'linkedin': 'linkedin.com',
      'spotify': 'spotify.com',
      'airbnb': 'airbnb.com',
      'dropbox': 'dropbox.com',
      'github': 'github.com',
      'atlassian': 'atlassian.com',
      'twilio': 'twilio.com',
      'hubspot': 'hubspot.com',
      'zendesk': 'zendesk.com',
      'datadog': 'datadoghq.com',
      'snowflake': 'snowflake.com',
      'mongodb': 'mongodb.com',
      'elastic': 'elastic.co',
      'cloudflare': 'cloudflare.com',
      'square': 'squareup.com',
      'block': 'block.xyz',
      'target': 'target.com',
      'walmart': 'walmart.com',
      'costco': 'costco.com',
      'home depot': 'homedepot.com',
      'lowes': 'lowes.com',
      'best buy': 'bestbuy.com',
      'starbucks': 'starbucks.com',
      'mcdonalds': 'mcdonalds.com',
      'nike': 'nike.com',
      'adidas': 'adidas.com',
    };
    
    const lowerName = name.toLowerCase().trim();
    
    // Only return mapped domains - don't guess for unknown companies
    // This prevents 404 spam for small/local businesses
    if (domainMappings[lowerName]) return domainMappings[lowerName];
    
    // For short, simple company names (likely well-known), try the domain
    // But skip long names or names with location hints (likely local businesses)
    const cleanName = lowerName.replace(/[^a-z0-9\s]/g, '').trim();
    const wordCount = cleanName.split(/\s+/).length;
    
    // Skip guessing if: too long (>2 words), or contains location hints
    const locationHints = ['llc', 'inc', 'corp', 'dba', 'franchise', 'location', 'branch', 'of ', 'in '];
    const hasLocationHint = locationHints.some(hint => lowerName.includes(hint));
    
    if (wordCount > 2 || hasLocationHint || cleanName.length > 20) {
      return ''; // Skip - likely a local business
    }
    
    // Single word or two short words - might be a known company
    return `${cleanName.replace(/\s+/g, '')}.com`;
  };
  
  // Get the best available company logo URL - works for all result types
  // Using Google's favicon service as it's more reliable than Clearbit
  const getCompanyLogoUrl = (): string | null => {
    // First, try explicit logo fields (including thumbnail for jobs)
    // Check nested company object first (ZoomInfo format)
    const explicitLogo = data.companyLogo || data.companyLogoUrl || data.logoUrl || 
      data.logo || data.company_logo || companyObj.logo || 
      data.thumbnail || data.image || data.imageUrl;
    if (explicitLogo) return explicitLogo;
    
    // Try to get domain from website - use Google's favicon service
    const domain = extractDomain(website);
    if (domain) {
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
    }
    
    // Try to guess domain from company name - handles job company names too
    const rawName = companyName || data.company_name || data.company;
    // Handle case where company is an object with a name property
    const nameToUse = typeof rawName === 'object' && rawName !== null ? (rawName as any).name : rawName;
    if (nameToUse && typeof nameToUse === 'string') {
      // Clean company name: remove suffixes like "in US", "US", location hints
      const cleanName = nameToUse
        .replace(/\s+(in\s+)?US$/i, '')
        .replace(/\s+in\s+\w+$/i, '')
        .trim();
      const guessedDomain = guessCompanyDomain(cleanName);
      if (guessedDomain) {
        return `https://www.google.com/s2/favicons?domain=${guessedDomain}&sz=128`;
      }
    }
    
    return null;
  };
  
  const companyLogo = getCompanyLogoUrl();
  
  // Full company data object for context links - pass all available info
  // so the drawer doesn't need to look it up again
  const fullCompanyData = {
    name: companyName,
    industry: companyIndustry || industry,
    location: companyCity && companyState ? `${companyCity}, ${companyState}` : (companyCity || companyState || location),
    employeeCount: employeeCount,
    revenue: revenue,
    website: website,
    logo: companyLogo,
    // Additional fields that might be available
    description: data.companyDescription || data.company_description || companyObj.description || '',
    foundedYear: data.foundedYear || data.founded_year || data.yearFounded || companyObj.foundedYear || '',
    linkedinUrl: data.companyLinkedinUrl || data.company_linkedin_url || companyObj.linkedinUrl || '',
  };
  
  // Get status info
  const statusInfo = LEAD_STATUSES.find(s => s.id === status) || LEAD_STATUSES[0];
  const StatusIcon = statusInfo.icon;

  // Find contacts at this company (for company/job/signal results)
  // For JOB results, use job title context to find relevant hiring managers
  const handleFindContacts = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!companyName && !fullName) return;
    
    setIsLoadingContacts(true);
    try {
      let contacts: any[] = [];
      
      // For SIGNAL results, use the dedicated enrichment endpoint
      if (isSignalResult) {
        const response = await api.post('/api/research/enrich-contacts', {
          companyName: companyName || fullName,
          industry: industry,
          query: signalType,
          rpp: 5,
        });
        
        // FIX: Handle multiple possible response structures from enrich-contacts
        // The backend may return: enrichedResults[].contacts, results[], or contacts[]
        if (response.data?.enrichedResults?.[0]?.contacts) {
          contacts = response.data.enrichedResults[0].contacts.map((c: any) => c.data || c);
        } else if (response.data?.results) {
          contacts = response.data.results.map((c: any) => c.data || c);
        } else if (response.data?.contacts) {
          contacts = response.data.contacts.map((c: any) => c.data || c);
        } else if (Array.isArray(response.data)) {
          contacts = response.data.map((c: any) => c.data || c);
        }
        
        // If still no contacts and we have a company name, fallback to decision-makers search
        if (contacts.length === 0 && companyName) {
          console.log('📋 Enrich-contacts returned no results, falling back to people search');
          const fallbackResponse = await api.post('/api/ai/ai-people-search', {
            query: `Decision makers at ${companyName}`,
            rpp: 5,
            searchMode: 'people',
          });
          contacts = fallbackResponse.data?.results?.people || fallbackResponse.data?.contacts || [];
        }
      } else if (isJobResult && jobTitle) {
        // For JOB results, infer the hiring manager/decision maker based on job title
        // Use STRUCTURED search with company name as filter, not embedded in query
        const jobLower = jobTitle.toLowerCase();
        let titleKeywords = '';
        let managementLevel = 'VP Level Exec';
        
        if (jobLower.includes('engineer') || jobLower.includes('developer') || jobLower.includes('software') || jobLower.includes('machine learning') || jobLower.includes('ai ')) {
          titleKeywords = 'VP Engineering, Engineering Manager, CTO, Head of Engineering, Director Engineering';
        } else if (jobLower.includes('sales') || jobLower.includes('account')) {
          titleKeywords = 'VP Sales, Sales Director, CRO, Head of Sales';
        } else if (jobLower.includes('marketing')) {
          titleKeywords = 'VP Marketing, Marketing Director, CMO, Head of Marketing';
        } else if (jobLower.includes('product')) {
          titleKeywords = 'VP Product, Product Director, CPO, Head of Product';
        } else if (jobLower.includes('design') || jobLower.includes('ux')) {
          titleKeywords = 'VP Design, Design Director, Head of Design, Creative Director';
        } else if (jobLower.includes('hr') || jobLower.includes('recruiter') || jobLower.includes('talent')) {
          titleKeywords = 'VP HR, HR Director, CHRO, Head of Talent, Head of People';
        } else if (jobLower.includes('finance') || jobLower.includes('accountant')) {
          titleKeywords = 'VP Finance, Finance Director, CFO, Controller';
        } else if (jobLower.includes('operations') || jobLower.includes('ops')) {
          titleKeywords = 'VP Operations, Operations Director, COO, Head of Operations';
        } else {
          // Default: find general decision makers at the company
          titleKeywords = 'VP, Director, Head of, Chief';
          managementLevel = 'Director';
        }
        
        // Use structured search - companyName as explicit filter
        const response = await api.post('/api/ai/ai-people-search', {
          query: titleKeywords,
          companyName: companyName, // CRITICAL: Pass company as structured filter
          rpp: 5,
          searchMode: 'people',
          // Pass additional structured criteria for better filtering
          structuredCriteria: {
            companyName: companyName,
            managementLevel: managementLevel,
            titleKeywords: titleKeywords.split(', '),
          }
        });
        contacts = response.data?.results?.people || response.data?.contacts || [];
      } else {
        // For other types (company results), use the standard decision maker search
        const response = await api.post('/api/ai/ai-people-search', {
          query: `Decision makers at ${companyName || fullName}`,
          rpp: 5,
          searchMode: 'people',
        });
        contacts = response.data?.results?.people || response.data?.contacts || [];
      }
      
      setFoundContacts(contacts.slice(0, 3));
      setShowContacts(true);
      
      if (onFindContacts) {
        onFindContacts(companyName || fullName, contacts);
      }
    } catch (err) {
      console.error('Failed to find contacts:', err);
    } finally {
      setIsLoadingContacts(false);
    }
  };

  // Find/infer decision maker (uses hiring manager inference)
  const handleFindDecisionMaker = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsLoadingContacts(true);
    try {
      // For job postings, infer the hiring manager from job title
      const response = await api.post('/api/ai/infer-decision-maker', {
        companyName: companyName || fullName,
        jobTitle: jobTitle,
        industry: industry,
      });
      
      if (response.data?.decisionMaker) {
        setFoundContacts([response.data.decisionMaker]);
        setShowContacts(true);
      }
      
      if (onFindDecisionMaker) {
        onFindDecisionMaker(companyName || fullName, response.data?.decisionMaker);
      }
    } catch (err) {
      console.error('Failed to find decision maker:', err);
      // Fallback to regular contact search
      handleFindContacts(e);
    } finally {
      setIsLoadingContacts(false);
    }
  };
  
  // Extract additional API data fields
  const managementLevel = data.managementLevel || data.management_level || '';
  const experienceYears = data.yearsExperience || data.years_experience || data.experienceYears || '';
  const contactAccuracyScore = data.contactAccuracyScore || data.contact_accuracy_score || data.accuracyScore || data.accuracy || null;
  const education = data.education || [];
  const linkedinUrl = data.linkedinUrl || data.linkedin_url || data.linkedInUrl || '';
  const employmentHistory = data.employmentHistory || data.employment_history || data.workExperience || data.work_experience || data.experience || [];
  const skills = data.skills || [];
  const certifications = data.certifications || [];
  
  // ContactOut rich data - summary/bio, headline, and work status
  const profileSummary = data.summary || data.bio || data.about || '';
  const headline = data.headline || data.headlineText || ''; // LinkedIn-style headline (e.g. "General Manager, Clinical Operations at CareFlight")
  const workStatus = data.workStatus || data.work_status || '';
  const isOpenToWork = workStatus === 'open_to_work' || data.openToWork || data.isOpenToWork;
  
  // ContactOut precision search indicator
  const isPrecisionMatch = data._precisionMatch || data.precisionMatch || false;
  const matchedKeywords = data._matchedKeywords || data.matchedKeywords || [];
  
  // Contact availability indicators (from ContactOut - shows if data exists without revealing it)
  const hasEmailAvailable = data.hasEmail || !!email;
  const hasPhoneAvailable = data.hasPhone || !!phone;
  
  // Animation classes for swipe effects - slide DOWN for archive, pulse for like
  const getAnimationClasses = () => {
    if (animationState === 'archiving') {
      // Slide DOWN and fade out (like closing a profile)
      return 'translate-y-[100%] opacity-0 scale-95';
    }
    if (animationState === 'liking') {
      return 'scale-[1.02] ring-2 ring-emerald-400 ring-offset-2';
    }
    return '';
  };

  // Get lead ID for scrolling target
  const leadId = lead.id || lead.personId || (lead.data?.id) || '';

  // Handle card click - gate for guests
  const handleCardClick = () => {
    if (shouldGateClick && onGuestClick) {
      onGuestClick();
      return;
    }
    if (isBulkMode && onBulkToggle) {
      onBulkToggle();
    } else if (onSelect) {
      onSelect();
    }
  };

  // Hide card completely after "Not a fit" animation completes
  if (isExiting) {
    return null;
  }

  return (
    <div
      data-lead-id={leadId}
      onClick={handleCardClick}
      className={`
        relative bg-white rounded-2xl border p-4 cursor-pointer 
        transition-all duration-200 ease-out
        ${shouldBlur ? 'select-none' : ''}
        ${isBulkSelected
          ? 'border-[#5a20ff] bg-[#5a20ff]/5 ring-2 ring-[#5a20ff]/20'
          : isSelected
            ? 'border-[#5a20ff] shadow-xl shadow-[#5a20ff]/15 ring-1 ring-[#5a20ff]/10'
            : status === 'liked'
              ? 'border-emerald-300 bg-emerald-50/50 shadow-sm'
              : status === 'archived'
                ? 'border-slate-200 bg-slate-50/80 opacity-60'
                : 'border-black/5 hover:border-[#5a20ff]/20 hover:shadow-lg hover:shadow-[#5a20ff]/5 hover:-translate-y-0.5'
        }
        ${getAnimationClasses()}
      `}
      style={{
        transform: animationState === 'archiving' ? 'translateY(100%) scale(0.95) rotate(-2deg)' : 
                   animationState === 'liking' ? 'scale(1.02)' : undefined,
        transition: animationState !== 'idle' ? 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)' : undefined,
        fontFamily: "'Playfair Display', serif",
      }}
    >
      {/* Guest blur overlay - show on cards after the first */}
      {shouldBlur && (
        <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] rounded-xl z-10 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center mx-auto mb-2 opacity-70">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="text-xs text-slate-500 font-medium">Sign up to view</span>
          </div>
        </div>
      )}
      <div className="flex items-start gap-4">
        {/* Bulk selection checkbox OR Avatar */}
        {isBulkMode ? (
          <div 
            className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-all ${
              isBulkSelected 
                ? 'bg-[var(--eq-purple)]' 
                : 'bg-black/5 hover:bg-[var(--eq-purple)]/20'
            }`}
          >
            {isBulkSelected ? (
              <Check className="w-6 h-6 text-white" />
            ) : (
              <div className="w-6 h-6 rounded border-2 border-black/20" />
            )}
          </div>
        ) : (
          /* Avatar with profile picture/logo and status indicator */
          <div className="relative flex-shrink-0">
            {/* Signal source logo - skip if already in failed cache */}
            {isSignalResult && signalSourceLogo && !failedLogoCache.has(signalSourceLogo) ? (
              <img 
                src={signalSourceLogo} 
                alt={companyName || 'Signal source'}
                className="w-14 h-14 rounded-xl object-cover bg-white border border-black/5 shadow-sm"
                onError={(e) => {
                  failedLogoCache.add(signalSourceLogo);
                  (e.target as HTMLImageElement).style.display = 'none';
                  const next = (e.target as HTMLImageElement).nextSibling as HTMLElement;
                  if (next) next.style.display = 'flex';
                }}
              />
            ) : (isCompanyResult || isJobResult) && companyLogo && !failedLogoCache.has(companyLogo) ? (
              /* Company logo for company results and job postings */
              <img 
                src={companyLogo} 
                alt={companyName || fullName}
                className="w-14 h-14 rounded-xl object-cover bg-white border border-black/5 shadow-sm"
                onError={(e) => {
                  failedLogoCache.add(companyLogo);
                  (e.target as HTMLImageElement).style.display = 'none';
                  const next = (e.target as HTMLImageElement).nextSibling as HTMLElement;
                  if (next) next.style.display = 'flex';
                }}
              />
            ) : profilePicture && !failedLogoCache.has(profilePicture) ? (
              /* Profile picture for people with company logo badge overlay */
              <div className="relative">
                <img 
                  src={profilePicture} 
                  alt={fullName}
                  className="w-14 h-14 rounded-full object-cover shadow-sm border-2 border-white"
                  onError={(e) => {
                    failedLogoCache.add(profilePicture);
                    (e.target as HTMLImageElement).style.display = 'none';
                    // Show fallback
                    const parent = (e.target as HTMLElement).parentElement;
                    if (parent) parent.style.display = 'none';
                    const next = parent?.nextSibling as HTMLElement;
                    if (next) next.style.display = 'flex';
                  }}
                />
                {/* Company logo badge - overlaid at bottom-right like enrichment product */}
                {companyLogo && !failedLogoCache.has(companyLogo) && (
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-md border border-black/5 p-0.5 flex items-center justify-center">
                    <img 
                      src={companyLogo} 
                      alt={companyName || ''}
                      className="w-full h-full rounded-full object-contain"
                      onError={(e) => {
                        failedLogoCache.add(companyLogo);
                        const badge = (e.target as HTMLElement).parentElement;
                        if (badge) badge.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            ) : null}
            {/* For people without profile pictures: show initials, NOT company logo */}
            {/* Fallback avatar - shown only if no image available or cached as failed */}
            {/* For people: show initials if no profile picture. For companies/jobs/signals: show logo or icon */}
            <div 
              className={`w-14 h-14 ${isCompanyResult || isSignalResult || isJobResult ? 'rounded-xl' : 'rounded-full'} bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center text-white text-lg font-bold shadow-sm`}
              style={{ 
                display: (
                  // For people: only hide if profile picture exists
                  (!isCompanyResult && !isSignalResult && !isJobResult && profilePicture && !failedLogoCache.has(profilePicture)) ||
                  // For companies/jobs: hide if company logo exists
                  ((isCompanyResult || isJobResult) && companyLogo && !failedLogoCache.has(companyLogo)) ||
                  // For signals: hide if signal source logo exists
                  (isSignalResult && signalSourceLogo && !failedLogoCache.has(signalSourceLogo))
                ) ? 'none' : 'flex' 
              }}
            >
              {isSignalResult ? (
                <TrendingUp className="w-6 h-6" />
              ) : isCompanyResult ? (
                <Building2 className="w-6 h-6" />
              ) : isJobResult ? (
                <Briefcase className="w-6 h-6" />
              ) : (
                fullName.charAt(0).toUpperCase()
              )}
              {/* Company logo badge on initials - for people without profile pictures */}
              {!isCompanyResult && !isJobResult && !isSignalResult && companyLogo && !failedLogoCache.has(companyLogo) && (!profilePicture || failedLogoCache.has(profilePicture)) && (
                <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white shadow-md border border-black/5 p-0.5 flex items-center justify-center">
                  <img 
                    src={companyLogo} 
                    alt={companyName || ''}
                    className="w-full h-full rounded-full object-contain"
                    onError={(e) => {
                      failedLogoCache.add(companyLogo);
                      const badge = (e.target as HTMLElement).parentElement;
                      if (badge) badge.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            {/* Status dot */}
            <div 
              className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center shadow-sm"
              style={{ backgroundColor: statusInfo.color }}
              title={statusInfo.label}
            >
              <StatusIcon className="w-3 h-3 text-white" />
            </div>
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {/* Title/Name - Clickable for contextual search */}
              <h3 className="text-base font-semibold truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isJobResult ? (
                  <ContextLink 
                    type="job" 
                    data={{ title: jobTitle, company: companyName, location, industry }}
                    className="text-left"
                  >
                    {jobTitle || 'Job Opening'}
                  </ContextLink>
                ) : isSignalResult || isNewsResult ? (
                  <span className="text-black line-clamp-2 text-left">
                    {signalHeadline || companyName || 'Signal'}
                  </span>
                ) : isCompanyResult ? (
                  <ContextLink 
                    type="company" 
                    data={fullCompanyData}
                    className="text-black hover:text-[var(--eq-purple-text)]"
                  >
                    {companyName || fullName}
                  </ContextLink>
                ) : isCompanyModeResult ? (
                  /* Company mode: Company name is the primary title */
                  <ContextLink 
                    type="company" 
                    data={fullCompanyData}
                    className="text-black hover:text-[var(--eq-purple-text)]"
                  >
                    {companyName || fullName}
                  </ContextLink>
                ) : (
                  <ContextLink 
                    type="person" 
                    data={{ name: fullName, title: jobTitle, company: companyName, industry, location }}
                    className="text-black hover:text-[var(--eq-purple-text)]"
                  >
                    {fullName}
                  </ContextLink>
                )}
              </h3>
              
              {/* Company mode: industry + size line under company name */}
              {isCompanyModeResult && (companyIndustry || employeeCount) && (
                <p className="text-xs text-indigo-600/70 truncate mt-0.5 flex items-center gap-1.5">
                  {companyIndustry && <span>{companyIndustry}</span>}
                  {companyIndustry && employeeCount && <span className="text-black/20">·</span>}
                  {employeeCount && <span>{employeeCount} employees</span>}
                </p>
              )}
              
              {/* Subtitle varies by type - company names are clickable for contextual search */}
              <p className="text-sm text-slate-600 truncate mt-0.5">
                {isSignalResult ? (
                  <span className="flex items-center gap-1.5 text-slate-500">
                    {isNewSignal && (
                      <span className="px-1.5 py-0.5 bg-red-500 text-white rounded text-[10px] font-bold">NEW</span>
                    )}
                    {companyName && (
                      <ContextLink 
                        type="company" 
                        data={fullCompanyData}
                        className="text-[var(--eq-purple-text)] font-medium hover:underline"
                      >
                        {companyName}
                      </ContextLink>
                    )}
                    {companyName && signalDate && <span className="text-black/20">·</span>}
                    {signalDate && <span>{signalDate}</span>}
                  </span>
                ) : isCompanyResult ? (
                  <>
                    {industry && (
                      <ContextLink type="industry" data={{ name: industry }} className="text-slate-600">
                        {industry}
                      </ContextLink>
                    )}
                    {industry && employeeCount ? ` · ${employeeCount} employees` : ''}
                  </>
                ) : isJobResult ? (
                  <>
                    {companyName && (
                      <ContextLink 
                        type="company" 
                        data={fullCompanyData}
                      >
                        {companyName}
                      </ContextLink>
                    )}
                    {remoteType ? ` · ${remoteType}` : ''}
                  </>
                ) : isCompanyModeResult ? (
                  /* Company mode: Person name + title as subtitle */
                  <>
                    <ContextLink 
                      type="person" 
                      data={{ name: fullName, title: jobTitle, company: companyName, industry, location }}
                      className="text-slate-700 font-medium hover:text-[var(--eq-purple-text)]"
                    >
                      {fullName}
                    </ContextLink>
                    {fullName && jobTitle && ' — '}
                    {jobTitle && (
                      <span className="text-slate-500">{jobTitle}</span>
                    )}
                  </>
                ) : (
                  <>
                    {jobTitle && (
                      <ContextLink 
                        type="job" 
                        data={{ title: jobTitle, company: companyName, industry }}
                        className="text-slate-600 hover:text-[var(--eq-purple-text)]"
                      >
                        <HighlightedText 
                          text={jobTitle} 
                          keywords={keywordMatches?.jobTitle || []} 
                        />
                      </ContextLink>
                    )}
                    {jobTitle && companyName && ' at '}
                    {companyName && (
                      <ContextLink 
                        type="company" 
                        data={fullCompanyData}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                      >
                        <HighlightedText 
                          text={companyName} 
                          keywords={keywordMatches?.companyName || []} 
                        />
                      </ContextLink>
                    )}
                  </>
                )}
              </p>
              
              {/* Headline - one-line professional tagline (ContactOut, LinkedIn); only when different from title+company */}
              {!isCompanyResult && !isCompanyModeResult && !isJobResult && !isSignalResult && headline && headline.trim() && headline !== jobTitle && (
                <p className="text-xs text-slate-600 mt-1 line-clamp-1">
                  {headline}
                </p>
              )}
              
              {/* Location row - respects field visibility */}
              {location && isFieldVisible('location', isCompanyResult ? 'company' : 'people') && (
                <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {location}
                </p>
              )}

              {/* Additional API data fields as pills - People results only - respects field visibility */}
              {!isCompanyResult && !isJobResult && !isSignalResult && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {/* Open to Work badge - from ContactOut */}
                  {isOpenToWork && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-semibold bg-green-100 text-green-700 border border-green-200 animate-pulse">
                      <Target className="w-3.5 h-3.5" />
                      Open to Work
                    </span>
                  )}
                  {/* Precision Match indicator */}
                  {isPrecisionMatch && matchedKeywords.length > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200" title={`Matched: ${matchedKeywords.join(', ')}`}>
                      <Sparkles className="w-3.5 h-3.5" />
                      Keyword Match
                    </span>
                  )}
                  {managementLevel && isFieldVisible('managementLevel', 'people') && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-violet-100 text-[var(--eq-purple-text)] border border-violet-200">
                      <Crown className="w-3.5 h-3.5" />
                      {Array.isArray(managementLevel) ? managementLevel[0] : managementLevel}
                    </span>
                  )}
                  {experienceYears && isFieldVisible('experienceYears', 'people') && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                      <Briefcase className="w-3.5 h-3.5" />
                      {experienceYears} years
                    </span>
                  )}
                  {education && education.length > 0 && (education[0]?.school || education[0]?.degree || education[0]?.description) && isFieldVisible('education', 'people') && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                      <GraduationCap className="w-3.5 h-3.5" />
                      {education[0].school || education[0].degree || education[0].description}
                    </span>
                  )}
                  {linkedinUrl && isFieldVisible('linkedinUrl', 'people') && (
                    <a
                      href={linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                    >
                      <Linkedin className="w-3.5 h-3.5" />
                      InMail
                    </a>
                  )}
                  {/* Contact availability indicators - shows data exists without revealing */}
                  {hasEmailAvailable && !email && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-200" title="Email available">
                      <Mail className="w-3 h-3" />
                      <span className="text-[10px]">Email</span>
                    </span>
                  )}
                  {hasPhoneAvailable && !phone && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-600 border border-emerald-200" title="Phone available">
                      <Phone className="w-3 h-3" />
                      <span className="text-[10px]">Phone</span>
                    </span>
                  )}
                  {/* Custom user-added fields (UGC) - always shown when present */}
                  {customFields && customFields.length > 0 && customFields.map((field: any) => (
                    <span 
                      key={field.id || field.name}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-[var(--eq-teal)]/15 text-[var(--eq-teal-text)] border border-[var(--eq-teal)]/30"
                      title={`${field.name}: ${field.value}`}
                    >
                      {field.name}: {field.value?.length > 15 ? `${field.value.slice(0, 15)}...` : field.value}
                    </span>
                  ))}
                </div>
              )}
              
              {/* Company/Job-specific pills - consistent with people card format */}
              {(isCompanyResult || isJobResult) && (
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {industry && (
                    <ContextLink 
                      type="industry" 
                      data={{ name: typeof industry === 'string' ? industry : 'Industry' }}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-violet-100 text-[var(--eq-purple-text)] border border-violet-200 hover:bg-violet-200"
                    >
                      <Building2 className="w-3.5 h-3.5" />
                      {typeof industry === 'string' ? industry : 'Industry'}
                    </ContextLink>
                  )}
                  {employeeCount && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                      <Users className="w-3.5 h-3.5" />
                      {employeeCount} employees
                    </span>
                  )}
                  {revenue && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                      <DollarSign className="w-3.5 h-3.5" />
                      {revenue}
                    </span>
                  )}
                  {website && (
                    <a
                      href={website.startsWith('http') ? website : `https://${website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      Website
                    </a>
                  )}
                  {isJobResult && salary && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                      <DollarSign className="w-3.5 h-3.5" />
                      {salary}
                    </span>
                  )}
                  {isJobResult && remoteType && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium bg-[var(--eq-teal)]/15 text-[var(--eq-teal-text)] border border-[var(--eq-teal)]/30">
                      <MapPin className="w-3.5 h-3.5" />
                      {remoteType}
                    </span>
                  )}
                </div>
              )}
              
              {/* Employment History / Work Experience - with REAL company logos */}
              {!isCompanyResult && !isJobResult && !isSignalResult && employmentHistory && employmentHistory.length > 0 && isFieldVisible('employmentHistory', 'people') && (
                <div className="mt-3 pt-2 border-t border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-2">Work Experience</p>
                  <div className="space-y-2">
                    {employmentHistory.slice(0, 4).map((job: any, idx: number) => {
                      // Safely extract company name - don't show if unknown
                      const jobCompanyName = typeof job.company === 'object' 
                        ? (job.company?.companyName || job.company?.name || job.companyName || '')
                        : (job.company || job.companyName || '');
                      
                      // Generate REAL company logo URL from Clearbit using API-provided website
                      const getJobCompanyLogo = () => {
                        // Try explicit logo first
                        if (job.companyLogo || job.company?.logo || job.company?.logoUrl) {
                          return job.companyLogo || job.company?.logo || job.company?.logoUrl;
                        }
                        
                        // USE THE API-PROVIDED WEBSITE - using Google's favicon service
                        const companyWebsite = job.company?.companyWebsite || job.company?.website || job.companyWebsite;
                        if (companyWebsite) {
                          // Extract clean domain from website URL
                          let domain = companyWebsite.replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];
                          if (domain) {
                            return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
                          }
                        }
                        
                        // Last resort: use guessCompanyDomain for well-known companies only
                        const guessedDomain = guessCompanyDomain(jobCompanyName);
                        if (guessedDomain) {
                          return `https://www.google.com/s2/favicons?domain=${guessedDomain}&sz=128`;
                        }
                        return null; // Don't generate a bad URL for unknown companies
                      };
                      const jobCompanyLogo = getJobCompanyLogo();
                      
                      return (
                        <div key={idx} className="py-1.5 border-b border-slate-50 last:border-0">
                          {/* Work experience - title and company on same line for clarity */}
                          <div className="flex items-start gap-2">
                            {/* Company logo thumbnail */}
                            {jobCompanyLogo && (
                              <img 
                                src={jobCompanyLogo} 
                                alt="" 
                                className="w-4 h-4 rounded mt-0.5 bg-slate-100 flex-shrink-0"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              {/* Title at company - single line; fallback to description for unstructured data */}
                              <p className="text-xs leading-tight">
                                {(job.title || job.jobTitle) ? (
                                  <>
                                    <span className="font-medium text-slate-700">{job.title || job.jobTitle}</span>
                                    {jobCompanyName && (
                                      <><span className="text-slate-400"> at </span><span className="text-[var(--eq-purple-text)] font-medium">{jobCompanyName}</span></>
                                    )}
                                  </>
                                ) : jobCompanyName ? (
                                  <span className="text-[var(--eq-purple-text)] font-medium">{jobCompanyName}</span>
                                ) : job.description ? (
                                  <span className="font-medium text-slate-700">{job.description}</span>
                                ) : null}
                              </p>
                              {/* Date range */}
                              {(job.startDate || job.start_date || job.fromDate) && (
                                <p className="text-[10px] text-slate-400">
                                  {formatDate(job.startDate || job.start_date || job.fromDate)}{(job.endDate || job.end_date || job.toDate) ? ` - ${formatDate(job.endDate || job.end_date || job.toDate)}` : ' - Present'}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {employmentHistory.length > 4 && (
                      <p className="text-[10px] text-slate-400 italic mt-1">+{employmentHistory.length - 4} more positions</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Profile Summary/Bio - from ContactOut rich data */}
              {!isCompanyResult && !isJobResult && !isSignalResult && profileSummary && isFieldVisible('summary', 'people') && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-500 mb-1">About</p>
                  <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                    {profileSummary}
                  </p>
                </div>
              )}
              
              {/* Skills - clickable for searching people with similar skills */}
              {!isCompanyResult && !isJobResult && !isSignalResult && skills && skills.length > 0 && isFieldVisible('skills', 'people') && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <p className="text-[10px] font-semibold text-slate-500 mb-1.5">Skills</p>
                  <div className="flex flex-wrap gap-1.5">
                    {skills.slice(0, 8).map((skill: string, idx: number) => (
                      <ContextLink 
                        key={idx} 
                        type="skill" 
                        data={{ name: skill, location }}
                        className="px-2 py-0.5 bg-slate-100 text-slate-700 hover:bg-[var(--eq-purple)]/10 hover:text-[var(--eq-purple-text)] rounded text-[10px] font-medium"
                      >
                        {skill}
                      </ContextLink>
                    ))}
                    {skills.length > 8 && (
                      <span className="text-[10px] text-slate-400 font-medium">+{skills.length - 8}</span>
                    )}
                  </div>
                </div>
              )}
              
              {/* Rich Profile indicator - shows when rich data exists from any source */}
              {!isCompanyResult && !isJobResult && !isSignalResult && (employmentHistory.length > 4 || profileSummary) && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSelect) onSelect();
                    }}
                    className="flex items-center gap-1.5 text-[10px] text-[var(--eq-purple-text)] hover:underline"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>View full profile with {employmentHistory.length} positions & achievements</span>
                  </button>
                </div>
              )}
              
              {/* News/Signal Summary - DEX: Clean, minimal, actionable */}
              {(isSignalResult || isNewsResult) && (
                <div className="mt-3 space-y-2">
                  {/* Signal badges row - minimal, no repetition */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Only show signal type ONCE as primary badge */}
                    {signalType && signalType !== 'news' && signalType !== 'News' && (
                      <span className="px-2 py-0.5 bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)] rounded text-xs font-semibold">
                        {signalType}
                      </span>
                    )}
                    {/* Demand Zone Badge - urgency indicator */}
                    {(demandZone || outreachTiming?.urgency) && (
                      <DemandZoneBadge 
                        zone={outreachTiming?.urgency || demandZone} 
                        size="sm" 
                      />
                    )}
                    {/* Funding amount if available */}
                    {fundingAmount && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                        <DollarSign className="w-3 h-3" />
                        {fundingAmount}
                      </span>
                    )}
                  </div>
                  
                  {/* Article snippet - THE ACTUAL CONTENT (not placeholder) */}
                  {(signalSnippet || signalHeadline) && (
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">
                      {signalSnippet || signalHeadline}
                    </p>
                  )}
                  
                  {/* Source link - ONLY clickable element for article */}
                  {signalSourceUrl && (
                    <a 
                      href={signalSourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 text-xs text-[var(--eq-purple-text)] hover:underline"
                    >
                      {signalSourceLogo && !failedLogoCache.has(signalSourceLogo) && (
                        <img 
                          src={signalSourceLogo}
                          alt=""
                          className="w-4 h-4 rounded object-contain"
                          onError={(e) => {
                            failedLogoCache.add(signalSourceLogo);
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      {signalSource || 'Read article'}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  
                  {/* Hiring Prediction - KEY VALUE for staffing */}
                  {(hiringPrediction || expectedHires) && (
                    <HiringPredictionCard
                      prediction={hiringPrediction || { expectedHires }}
                      timing={outreachTiming}
                      fundingAmount={fundingAmount}
                      compact={true}
                    />
                  )}
                </div>
              )}
              
              {/* Extra fields for Company/Job/Signal results - respects field visibility */}
              {(isCompanyResult || isJobResult || isSignalResult) && (
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  {/* Signal source link */}
                  {isSignalResult && signalSourceUrl && (
                    <a
                      href={signalSourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] text-[var(--eq-purple-text)] flex items-center gap-1 hover:underline"
                    >
                      <ExternalLink className="w-2.5 h-2.5" />
                      {signalSource || 'Source'}
                    </a>
                  )}
                  {/* Demand score badge for signals */}
                  {isSignalResult && demandScore > 0 && isFieldVisible('demandScore', 'agent') && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex items-center gap-1 ${
                      demandZone === 'hot' ? 'bg-red-100 text-red-600' :
                      demandZone === 'perfect' ? 'bg-[var(--eq-teal)]/20 text-[var(--eq-teal-text)]' :
                      demandZone === 'warm' ? 'bg-yellow-100 text-yellow-600' :
                      'bg-black/5 text-black/50'
                    }`}>
                      <Zap className="w-2.5 h-2.5" />
                      {demandScore}% match
                    </span>
                  )}
                  {revenue && !isSignalResult && isFieldVisible('revenue', 'company') && (
                    <span className="text-[10px] text-black/40 flex items-center gap-1">
                      <DollarSign className="w-2.5 h-2.5" />
                      {revenue}
                    </span>
                  )}
                  {salary && isFieldVisible('salary', 'jobs') && (
                    <span className="text-[10px] text-[var(--eq-teal-text)] flex items-center gap-1 font-medium">
                      <DollarSign className="w-2.5 h-2.5" />
                      {salary}
                    </span>
                  )}
                  {postedDate && isFieldVisible('postedDate', 'jobs') && (
                    <span className="text-[10px] text-black/40 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {postedDate}
                    </span>
                  )}
                  {website && !isSignalResult && isFieldVisible('website', 'company') && (
                    <a
                      href={website.startsWith('http') ? website : `https://${website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] text-[var(--eq-purple-text)] flex items-center gap-1 hover:underline"
                    >
                      <Globe className="w-2.5 h-2.5" />
                      Website
                    </a>
                  )}
                  {/* Apply link - ONLY for job results, not signals */}
                  {isJobResult && sourceUrl && isFieldVisible('sourceUrl', 'jobs') && (
                    <a
                      href={sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] text-[var(--eq-purple-text)] flex items-center gap-1 hover:underline"
                    >
                      <ExternalLink className="w-2.5 h-2.5" />
                      {location ? `Apply in ${location}` : 'Apply Now'}
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Lead qualification buttons - Skip / Like */}
            {onStatusChange && !isBulkMode && (
              <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {/* Main action row: Skip and Like */}
                <div className="flex items-center gap-2">
                  {/* Skip - X - Fast swipe out and HIDE */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnimationState('archiving');
                      onStatusChange('archived');
                      // After animation completes, hide the card completely
                      setTimeout(() => {
                        setIsExiting(true);
                      }, 350); // Match animation duration
                    }}
                    disabled={animationState !== 'idle'}
                    className={`w-12 h-12 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-150 touch-manipulation ${
                      status === 'archived'
                        ? 'bg-slate-500 text-white shadow-lg shadow-slate-400/40'
                        : 'bg-white border border-slate-200 text-slate-400 hover:border-red-300 hover:bg-red-50 hover:text-red-500 active:scale-95'
                    } ${animationState === 'archiving' ? 'scale-95 bg-red-500 text-white border-red-500' : ''}`}
                    title="Skip"
                    aria-label="Skip this lead"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  
                  {/* Like / Add to Reach — people get the Reach icon, others get checkmark */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setAnimationState('liking');
                      onStatusChange('liked');
                      setTimeout(() => {
                        setAnimationState('idle');
                        if (onSelect) onSelect();
                      }, 400);
                    }}
                    disabled={animationState !== 'idle'}
                    className={`w-12 h-12 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-200 touch-manipulation ${
                      status === 'liked'
                        ? (!isCompanyResult && !isJobResult && !isNewsResult && !isSignalResult
                            ? 'bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white shadow-lg shadow-[#5a20ff]/40'
                            : 'bg-emerald-500 text-white shadow-lg shadow-emerald-400/50')
                        : (!isCompanyResult && !isJobResult && !isNewsResult && !isSignalResult
                            ? 'bg-white border border-slate-200 text-slate-400 hover:border-[#5a20ff] hover:text-[#5a20ff] hover:bg-[#5a20ff]/5 active:scale-95'
                            : 'bg-white border border-slate-200 text-slate-400 hover:border-emerald-400 hover:text-emerald-500 hover:bg-emerald-50 active:scale-95')
                    } ${animationState === 'liking' && status !== 'liked' ? (
                      !isCompanyResult && !isJobResult && !isNewsResult && !isSignalResult
                        ? 'animate-celebrate scale-110 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white shadow-xl shadow-[#5a20ff]/40'
                        : 'animate-celebrate scale-110 bg-emerald-500 text-white border-emerald-500 shadow-xl shadow-emerald-400/50'
                    ) : ''}`}
                    title={!isCompanyResult && !isJobResult && !isNewsResult && !isSignalResult ? 'Add to Reach' : 'Like'}
                    aria-label={!isCompanyResult && !isJobResult && !isNewsResult && !isSignalResult ? 'Add to Reach' : 'Like this lead'}
                  >
                    {animationState === 'liking' && status !== 'liked' ? (
                      <Sparkles className="w-5 h-5 animate-spin" />
                    ) : !isCompanyResult && !isJobResult && !isNewsResult && !isSignalResult ? (
                      <Send className="w-5 h-5" />
                    ) : (
                      <Check className="w-5 h-5" />
                    )}
                  </button>
                </div>
                
                {/* "Outreach Now" reminder when liked on a signal */}
                {status === 'liked' && (isSignalResult || isNewsResult) && (demandZone === 'act_now' || demandZone === 'high' || outreachTiming?.urgency === 'HIGH') && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.dispatchEvent(new CustomEvent('eq:quick-outreach', {
                        detail: { 
                          companyName,
                          signalType,
                          hiringPrediction,
                          intent: 'opportunity'
                        }
                      }));
                    }}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-500 text-white rounded-full text-[10px] font-semibold 
                               animate-pulse hover:bg-amber-600 transition-all"
                  >
                    <Zap className="w-3 h-3" />
                    Outreach Now
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Action buttons - Refine Search removed, use blue links instead */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {/* Signal Actions - Find Company → Find Decision Makers flow */}
            {/* Only show if we extracted a real company name (not news source) */}
            {(isSignalResult || isNewsResult) && (
              <>
                {/* Find Company - search for the company mentioned in the signal */}
                {companyName && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Dispatch event to search for this company
                      window.dispatchEvent(new CustomEvent('eq:quick-search', {
                        detail: { query: companyName, mode: 'company' }
                      }));
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--eq-teal)]/10 text-[var(--eq-teal-text)] hover:bg-[var(--eq-teal)]/20 transition-colors text-xs font-medium"
                    title={`Search for ${companyName}`}
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    {companyName}
                  </button>
                )}
                
                {/* Find Decision Makers - search for people at this company who make hiring decisions */}
                {companyName && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Get target titles from hiring prediction if available
                      const targetTitles = hiringPrediction?.targetTitles || ['VP', 'Director', 'Head of'];
                      // Build a proper people search query
                      const searchQuery = `${targetTitles[0]} ${companyName}`;
                      // IMPORTANT: Switch to people mode for people search
                      window.dispatchEvent(new CustomEvent('eq:quick-search', {
                        detail: { 
                          query: searchQuery, 
                          mode: 'people'  // Force people mode for people search
                        }
                      }));
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)] hover:bg-[var(--eq-purple)]/20 transition-colors text-xs font-medium"
                    title={`Find people who make hiring decisions at ${companyName}`}
                  >
                    <Users className="w-3.5 h-3.5" />
                    Find Contacts
                  </button>
                )}
              </>
            )}
            
            {/* Find Similar — temporarily disabled
            {cardIndex > 0 && onFindSimilar && !isCompanyResult && !isJobResult && !isNewsResult && !isSignalResult && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const keyElements = extractKeyElementsForSimilar(lead);
                  const filters = convertKeyElementsToFilters(keyElements, searchMode);
                  onFindSimilar(filters, keyElements);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#5a20ff]/10 text-[#5a20ff] hover:bg-[#5a20ff]/20 transition-colors text-xs font-medium"
                title={getSimilarLookupExplanation(extractKeyElementsForSimilar(lead))}
              >
                <Users className="w-3.5 h-3.5" />
                Find Similar
              </button>
            )}
            */}
            
            {/* Reach button removed — the like button IS the reach action now */}
            {!isCompanyResult && !isJobResult && !isNewsResult && !isSignalResult && email && isFieldVisible('email', 'people') && (
              <a
                href={`mailto:${email}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)] hover:bg-[var(--eq-purple)]/20 transition-colors"
                title="Send email"
              >
                <Mail className="w-4 h-4" />
              </a>
            )}
            {!isCompanyResult && !isJobResult && !isNewsResult && phone && isFieldVisible('phone', 'people') && (
              <a
                href={`tel:${phone}`}
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-[var(--eq-teal)]/10 text-[var(--eq-teal-text)] hover:bg-[var(--eq-teal)]/20 transition-colors"
                title="Call"
              >
                <Phone className="w-4 h-4" />
              </a>
            )}
            {industry && !isCompanyResult && isFieldVisible('industry', 'people') && (
              <ContextLink 
                type="industry" 
                data={{ name: industry }}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-black/5 hover:bg-[var(--eq-purple)]/10 rounded-full text-xs text-black/50 hover:text-[var(--eq-purple-text)] font-medium"
              >
                <Briefcase className="w-3 h-3" />
                {industry}
              </ContextLink>
            )}
          </div>
        </div>
      </div>

      {/* Found Contacts Section - Full lead card functionality */}
      {showContacts && foundContacts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[10px] text-slate-500 uppercase font-medium tracking-wide">
              Decision Makers Found ({foundContacts.length})
            </span>
            <button
              onClick={(e) => { e.stopPropagation(); setShowContacts(false); }}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {foundContacts.map((contact, idx) => {
              const contactData = contact.data || contact;
              const contactName = contactData.name || contactData.fullName || 
                `${contactData.firstName || ''} ${contactData.lastName || ''}`.trim() || '';
              const contactTitle = contactData.jobTitle || contactData.title || '';
              const contactEmail = contactData.email || contactData.emailAddress || '';
              const contactPhone = contactData.phone || contactData.directPhone || contactData.mobilePhone || '';
              const contactLinkedIn = contactData.linkedIn || contactData.linkedInUrl || contactData.linkedin || '';
              const contactCompany = contactData.companyName || contactData.company || companyName || '';
              const contactId = contactData.id || contactData.personId || `contact_${idx}`;
              const hasContactInfo = contactEmail || contactPhone;
              const isEnriched = !!(contactEmail && contactPhone);
              
              return (
                <div 
                  key={idx} 
                  className="p-3 bg-gradient-to-br from-violet-50 to-white border border-violet-100 rounded-xl hover:shadow-md transition-all"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Dispatch event to select this contact as a lead
                    window.dispatchEvent(new CustomEvent('eq:select-lead', { 
                      detail: { 
                        lead: { id: contactId, type: 'contact', data: contactData },
                        source: 'found-contacts'
                      }
                    }));
                  }}
                >
                  {/* Contact Header */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                      {contactName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{contactName}</p>
                      <p className="text-xs text-slate-600 truncate">{contactTitle}</p>
                      {contactCompany && (
                        <p className="text-xs text-slate-400 truncate">{contactCompany}</p>
                      )}
                    </div>
                    {/* Enriched badge */}
                    {isEnriched && (
                      <span className="px-2 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 rounded-full">
                        Enriched
                      </span>
                    )}
                  </div>
                  
                  {/* Contact Details - Show if available */}
                  {hasContactInfo && (
                    <div className="mb-3 p-2 bg-white rounded-lg border border-slate-100 space-y-1.5">
                      {contactEmail && (
                        <div className="flex items-center gap-2 text-xs">
                          <Mail className="w-3.5 h-3.5 text-[var(--eq-purple)]" />
                          <a href={`mailto:${contactEmail}`} className="text-slate-700 hover:text-[var(--eq-purple)] truncate">
                            {contactEmail}
                          </a>
                        </div>
                      )}
                      {contactPhone && (
                        <div className="flex items-center gap-2 text-xs">
                          <Phone className="w-3.5 h-3.5 text-[var(--eq-teal)]" />
                          <a href={`tel:${contactPhone}`} className="text-slate-700 hover:text-[var(--eq-teal)]">
                            {contactPhone}
                          </a>
                        </div>
                      )}
                      {contactLinkedIn && (
                        <div className="flex items-center gap-2 text-xs">
                          <Linkedin className="w-3.5 h-3.5 text-blue-600" />
                          <a href={contactLinkedIn} target="_blank" rel="noopener noreferrer" className="text-slate-700 hover:text-blue-600 truncate">
                            LinkedIn Profile
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    {/* Enrich Button - Show if not enriched */}
                    {!isEnriched && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const response = await api.post('/api/person-enrich/by-name', {
                              firstName: contactData.firstName || contactName.split(' ')[0],
                              lastName: contactData.lastName || contactName.split(' ').slice(1).join(' '),
                              companyName: contactCompany,
                            });
                            if (response.data?.contact) {
                              // Update the contact in foundContacts
                              setFoundContacts(prev => prev.map((c, i) => 
                                i === idx ? { ...c, data: { ...contactData, ...response.data.contact } } : c
                              ));
                            }
                          } catch (err) {
                            console.error('Enrich failed:', err);
                          }
                        }}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-[var(--eq-purple)] text-white text-xs font-medium rounded-lg hover:opacity-90 transition-opacity"
                      >
                        <Sparkles className="w-3.5 h-3.5" />
                        Enrich Contact
                      </button>
                    )}
                    
                    {/* Email Button - Show if has email */}
                    {contactEmail && (
                      <a
                        href={`mailto:${contactEmail}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 bg-violet-100 text-[var(--eq-purple-text)] text-xs font-medium rounded-lg hover:bg-violet-200 transition-colors"
                      >
                        <Mail className="w-3.5 h-3.5" />
                        Email
                      </a>
                    )}
                    
                    {/* LinkedIn Button */}
                    {contactLinkedIn && (
                      <a
                        href={contactLinkedIn}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        <Linkedin className="w-3.5 h-3.5" />
                        LinkedIn
                      </a>
                    )}
                    
                    {/* Phone Button */}
                    {contactPhone && (
                      <a
                        href={`tel:${contactPhone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg hover:bg-emerald-200 transition-colors"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        Call
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SimpleReach Modal - Message-first outreach */}
      {REACH_ENABLED && (
        <SimpleReach
          isOpen={showReachModal}
          onClose={() => setShowReachModal(false)}
          leadId={leadId}
          contact={{
            name: fullName,
            firstName: firstName,
            lastName: lastName,
            email: email,
            linkedin_url: linkedinUrl,
            title: jobTitle,
            companyName: companyName,
            industry: industry,
            location: location,
          }}
          onSendComplete={() => {
            setShowReachModal(false);
          }}
        />
      )}
    </div>
  );
}

export default LeadCard;
